import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useIsMobile } from "@/hooks/use-mobile";

// ─── Butterfly ───
interface ButterflyData {
  id: number;
  startX: number;
  startY: number;
  color1: string;
  color2: string;
  size: number;
  duration: number;
}

const BUTTERFLY_PALETTES = [
  { c1: "#FFB5C5", c2: "#FFA0B0" },
  { c1: "#7BAFD4", c2: "#5A8FB8" },
  { c1: "#F4D35E", c2: "#E8C040" },
  { c1: "#C97BDB", c2: "#A85DC0" },
  { c1: "#FFE4B5", c2: "#F0D090" },
];

const Butterflies = () => {
  const [butterflies, setButterflies] = useState<ButterflyData[]>([]);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (isMobile) return; // P0: skip butterflies on mobile to free GPU/CPU
    let idCounter = 0;
    const spawn = () => {
      const palette = BUTTERFLY_PALETTES[Math.floor(Math.random() * BUTTERFLY_PALETTES.length)];
      const b: ButterflyData = {
        id: idCounter++,
        startX: 15 + Math.random() * 70,
        startY: 20 + Math.random() * 50,
        color1: palette.c1,
        color2: palette.c2,
        size: 20 + Math.random() * 12,
        duration: 15 + Math.random() * 10,
      };
      setButterflies((prev) => [...prev, b]);
      setTimeout(() => setButterflies((prev) => prev.filter((x) => x.id !== b.id)), b.duration * 1000 + 500);
    };

    const first = setTimeout(() => {
      spawn();
      const interval = setInterval(() => spawn(), 25000 + Math.random() * 35000);
      return () => clearInterval(interval);
    }, 8000 + Math.random() * 12000);

    return () => clearTimeout(first);
  }, [isMobile]);

  if (isMobile) return null;

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 28 }}>
      <AnimatePresence>
        {butterflies.map((b) => (
          <motion.div
            key={b.id}
            className="absolute"
            style={{ left: `${b.startX}%`, top: `${b.startY}%` }}
            initial={{ opacity: 0 }}
            animate={{
              opacity: [0, 0.85, 0.85, 0],
              x: [0, 80, -60, 120, -40, 90],
              y: [0, -40, 20, -60, 30, -20],
            }}
            transition={{ duration: b.duration, ease: "easeInOut" }}
          >
            <svg width={b.size} height={b.size * 0.8} viewBox="0 0 40 32" fill="none">
              {/* Left wings */}
              <motion.ellipse
                cx="18" cy="12" rx="10" ry="8"
                fill={b.color1} fillOpacity="0.8"
                transform="rotate(-15 18 12)"
                animate={{ rx: [10, 3, 10] }}
                transition={{ duration: 0.4, repeat: Infinity, ease: "easeInOut" }}
              />
              <motion.ellipse
                cx="16" cy="22" rx="7" ry="6"
                fill={b.color2} fillOpacity="0.7"
                transform="rotate(-10 16 22)"
                animate={{ rx: [7, 2, 7] }}
                transition={{ duration: 0.4, repeat: Infinity, ease: "easeInOut" }}
              />
              {/* Right wings */}
              <motion.ellipse
                cx="22" cy="12" rx="10" ry="8"
                fill={b.color1} fillOpacity="0.8"
                transform="rotate(15 22 12)"
                animate={{ rx: [10, 3, 10] }}
                transition={{ duration: 0.4, repeat: Infinity, ease: "easeInOut" }}
              />
              <motion.ellipse
                cx="24" cy="22" rx="7" ry="6"
                fill={b.color2} fillOpacity="0.7"
                transform="rotate(10 24 22)"
                animate={{ rx: [7, 2, 7] }}
                transition={{ duration: 0.4, repeat: Infinity, ease: "easeInOut" }}
              />
              {/* Body */}
              <ellipse cx="20" cy="16" rx="1.5" ry="10" fill="#4A3F2E" opacity="0.7" />
              {/* Antennae */}
              <path d="M19,7 Q16,2 14,1" stroke="#4A3F2E" strokeWidth="0.6" fill="none" opacity="0.5" />
              <path d="M21,7 Q24,2 26,1" stroke="#4A3F2E" strokeWidth="0.6" fill="none" opacity="0.5" />
              <circle cx="14" cy="1" r="0.8" fill="#4A3F2E" opacity="0.5" />
              <circle cx="26" cy="1" r="0.8" fill="#4A3F2E" opacity="0.5" />
              {/* Wing patterns */}
              <circle cx="18" cy="11" r="3" fill={b.color2} fillOpacity="0.5" />
              <circle cx="22" cy="11" r="3" fill={b.color2} fillOpacity="0.5" />
            </svg>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};

export default function ForestFauna() {
  return (
    <>
      <Butterflies />
    </>
  );
}
